import { ReactNode, useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  CalendarDays,
  Check,
  Copy,
  ExternalLink,
  Plus,
  Sparkles,
  UserPlus,
  Loader2,
  RefreshCw,
  ArrowRight,
} from "lucide-react";
import { OnboardingChecklist } from "./OnboardingChecklist";
import { Heading, Text } from "@/components/Typography";
import { useToast } from "@/hooks/useToast";
import { useAuth } from "@/hooks/useAuth";
import { trackGettingStarted } from "../gettingStartedTracking";
import { api, type ProgramCoachPromptOut } from "@/services/api";
import { cn } from "@/utils/cn";
import {
  PROGRAM_COACH_PROMPT_TOKENS,
  REFERRALS_TARGET,
  REFERRALS_TO_COMPLETE,
  type GettingStartedBlock,
  type GettingStartedTask,
} from "@/constants/gettingStarted";
import {
  isStructuredStar,
  type StarPortfolioEntry,
  type StarPortfolioStory,
} from "@/utils/starPortfolio";

/** The step's primary action: a rounded rectangle, not a pill (design). */
const STEP_BUTTON =
  "inline-flex h-[44px] items-center gap-3 whitespace-nowrap rounded-[14px] bg-primary-purple px-6 text-[14px] font-semibold text-white transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50";

interface GettingStartedStepProps {
  task: GettingStartedTask;
  index: number;
  done: boolean;
  /** Done because the data says so, not because the user claimed it. */
  autoDone?: boolean;
  /** Steps in this phase, for the hint card's "01 of 05". */
  stepCount: number;
  /**
   * Bumped each time the user is brought to this step from outside the
   * journey (the sidebar dock, a deep link). The step scrolls into view and
   * plays the design's "arrive" pulse once per new value.
   */
  arriveKey?: number;
  /** Which control fired it, for analytics: Skip or "Mark complete". */
  onToggleDone: (method: "skip" | "mark_complete") => void;
  onAct: () => void;
  /**
   * The expert's S.T.A.R. portfolio (capabilities mirror), for the `stars`
   * block. The `starPicker` block fetches `stars` table rows itself.
   */
  stars: StarPortfolioEntry[];
}

/**
 * One step of the launch journey, shown one at a time (Getting Started
 * prototype, "All 4 Phases · Consistent Style"): a large step number and
 * title, the guidance on the left, the hint card on the right, and the action
 * row underneath.
 *
 * ACTION ROW — the behaviour is unchanged from the accordion it replaced:
 *   CTA            when the step has one and is not done (referral steps keep
 *                  theirs after completion: one ticks the step, three is the ask)
 *   Skip this one  when optional and not done
 *   Completed      when done (no Undo — a finished step stays finished)
 *   Already done?  when NOT DONE and the step sets `allowMarkComplete`
 */
export const GettingStartedStep = ({
  task,
  index,
  stepCount,
  done,
  arriveKey,
  onToggleDone,
  onAct,
  stars,
}: GettingStartedStepProps) => {
  const rootRef = useRef<HTMLDivElement>(null);

  // Arrival: bring the step into view, then replay the pulse. Removing the
  // class and forcing a reflow is what restarts a CSS animation that has
  // already run on this element.
  useEffect(() => {
    const el = rootRef.current;
    if (!el || !arriveKey) return;
    el.scrollIntoView({ behavior: "smooth", block: "start" });
    el.classList.remove("animate-gs-arrive");
    void el.offsetWidth;
    el.classList.add("animate-gs-arrive");
  }, [arriveKey]);

  // The first paragraph is the step's intro, set under the title.
  const [first, ...rest] = task.content;
  const intro = first?.kind === "p" ? first.text : null;
  const body = intro ? rest : task.content;
  const hintNotes =
    task.ctaPlacement === "hint" || task.asideNote
      ? body.filter((b): b is Extract<GettingStartedBlock, { kind: "note" }> => b.kind === "note")
      : [];
  const withoutNotes = hintNotes.length ? body.filter((b) => b.kind !== "note") : body;

  const sectionStarts = withoutNotes.reduce<number[]>((acc, b, i) => {
    if (b.kind === "h") acc.push(i);
    return acc;
  }, []);
  const asideCount = Math.min(task.asideSections ?? 0, sectionStarts.length);
  const asideStart = asideCount ? sectionStarts[sectionStarts.length - asideCount] : -1;
  const bodyBlocks = asideStart >= 0 ? withoutNotes.slice(0, asideStart) : withoutNotes;
  const asideBlocks = asideStart >= 0 ? withoutNotes.slice(asideStart) : [];

  const referralCount = useReferralList(task.referrals).length;
  const showCta = !!task.action && (task.referrals ? true : !done);
  const ctaLabel = task.referrals
    ? referralCount
      ? task.referrals === "xo"
        ? "Refer another Expert Operator"
        : "Refer another achiever"
      : task.cta
    : task.cta;

  const pad = (n: number) => String(n).padStart(2, "0");

  // The action row runs under the guidance only, under the hint card, or
  // across the whole card.
  const inColumn = task.ctaPlacement === "column";
  const inHint = task.ctaPlacement === "hint";
  const inSession = task.ctaPlacement === "session";

  const actionButtons = (
    <div className="flex flex-wrap items-center gap-[9px]">
      {showCta && (
        <button type="button" onClick={onAct} className={STEP_BUTTON}>
          {task.referrals && <UserPlus size={16} />}
          {ctaLabel}
          {!task.referrals &&
            (task.action?.kind === "external" ? (
              <ExternalLink size={16} />
            ) : task.ctaGlyph ? (
              <span aria-hidden>{task.ctaGlyph}</span>
            ) : (
              <ArrowRight size={16} aria-hidden />
            ))}
        </button>
      )}
    </div>
  );

  const actionStatus = (
    <div className="flex items-center gap-3 text-[10.5px] text-theme-text-muted">
      {done ? (
        <span className="inline-flex items-center gap-1.5 text-[11.5px] font-semibold text-theme-text-success">
          <Check size={13} strokeWidth={2.5} />
          Completed
        </span>
      ) : task.allowMarkComplete ? (
        <>
          <span>Already done?</span>
          <button
            type="button"
            onClick={() => onToggleDone("mark_complete")}
            className="inline-flex items-center gap-1 py-2 transition-colors hover:text-primary-purple"
          >
            Mark complete
            <span className="h-[9px] w-[9px] rounded-full border border-current" aria-hidden />
          </button>
        </>
      ) : null}
    </div>
  );

  const actionRow = (
    <div
      className={cn(
        "flex min-h-[57px] flex-shrink-0 flex-wrap items-center gap-3.5 border-t border-theme-border-subtle pt-[15px]",
        inSession ? "justify-end" : "justify-between",
        inColumn ? "mt-[30px]" : "mt-[22px]",
      )}
    >
      {!inSession && actionButtons}
      {actionStatus}
    </div>
  );

  const hintAction = (
    <div className="flex flex-1 flex-col items-center justify-center gap-2.5">
      {actionButtons}
      {actionStatus}
    </div>
  );

  const sessionAction = inSession ? actionButtons : undefined;

  const sessionStatus = (
    <div className="mt-auto flex min-h-[66px] items-center justify-center">{actionStatus}</div>
  );

  return (
    // Sized to its content, never scrolled inside, with Figma's 497px
    // (338:2711) as the floor so short steps keep the design's proportions.
    <div
      ref={rootRef}
      className="flex scroll-mt-4 flex-col overflow-hidden rounded-[14px] border border-[color-mix(in_srgb,var(--color-primary-purple)_22%,transparent)] bg-theme-bg-subtle px-4 py-5 sm:px-[37px] sm:pb-[20px] sm:pt-[26px] md:min-h-[390px]"
    >
      <div
        className={cn(
          "grid min-h-0 flex-1 grid-cols-1 items-stretch gap-5 md:grid-cols-[minmax(0,1fr)_248px]",
        )}
      >
        <div className="min-w-0">
          <div className="flex flex-wrap items-start gap-5">
            <span className="min-w-[39px] flex-shrink-0 whitespace-nowrap text-[30px] font-extrabold leading-9 text-primary-purple sm:text-[36px]">
              {pad(index)}
            </span>
            <div className="flex min-w-[260px] flex-1 flex-col gap-2.5">
              <Heading
                as="h2"
                className="text-[24px] font-bold leading-[30px] tracking-[-0.6px] text-theme-text-heading sm:text-[30px] sm:leading-9"
              >
                {task.title}
              </Heading>
              {intro && (
                <Text
                  as="p"
                  className="max-w-[643px] pt-1 text-[14px] leading-[23px] text-theme-text-secondary sm:text-[15px] sm:leading-[24.75px]"
                >
                  {intro}
                </Text>
              )}
            </div>
          </div>

          <StepBody blocks={bodyBlocks} stars={stars} sessionAction={sessionAction} />

          {asideBlocks.length > 0 && (
            <div className="md:hidden">
              <StepBody blocks={asideBlocks} stars={stars} />
            </div>
          )}

          {task.referrals && <ReferralProgress kind={task.referrals} />}

          {inColumn && actionRow}
        </div>

        <div className={cn("flex flex-col gap-[18px]", inHint ? "h-full" : "self-start")}>
          {hintNotes.map((note, n) => (
            <NoteBlock key={n} label={note.label} text={note.text} flush />
          ))}
          {task.hint.variant === "count" ? (
            // Figma 338:2977 — fixed 248×357 with the big "01 of 05".
            <aside className="hidden w-[248px] flex-col self-start rounded-2xl border border-[color-mix(in_srgb,var(--color-primary-purple)_30%,transparent)] bg-[color-mix(in_srgb,var(--color-primary-purple)_9%,var(--theme-bg-surface))] px-[19px] pb-[34px] pt-6 md:flex">
              <Text
                as="p"
                className="text-[9.5px] font-medium uppercase leading-[12px] tracking-[1.33px] text-theme-text-muted"
              >
                {task.hint.label}
              </Text>
              <Heading
                as="h3"
                className="mt-[49px] font-sarabun text-[19px] font-extrabold leading-[22.8px] tracking-[-0.855px] text-theme-text-heading"
              >
                {task.hint.title}
              </Heading>
              <Text
                as="p"
                className="mt-3.5 text-[10.5px] leading-[16.8px] text-theme-text-secondary"
              >
                {task.hint.text}
              </Text>
              <p className="mt-[18px] flex items-baseline gap-2">
                <span className="font-sarabun text-[46px] font-bold leading-[46px] tracking-[-2.76px] text-theme-text-heading">
                  {pad(index)}
                </span>
                <span className="text-[12px] font-semibold text-primary-purple">
                  of {pad(stepCount)}
                </span>
              </p>
              <Text
                as="p"
                className="mt-[18px] text-[9.5px] leading-[15.2px] text-theme-text-muted"
              >
                {task.hint.footer}
              </Text>
            </aside>
          ) : task.hint.variant === "panel" ? (
            // Figma 338:4207 — padded panel, icon on top.
            <aside className="hidden w-[248px] flex-col self-start rounded-2xl border border-[color-mix(in_srgb,var(--color-primary-purple)_18%,transparent)] bg-[color-mix(in_srgb,var(--color-primary-purple)_8%,var(--theme-bg-surface))] px-[19px] pb-7 pt-[26px] md:flex">
              {task.hint.icon && (
                <task.hint.icon size={22} strokeWidth={1.83} className="text-primary-purple" />
              )}
              <Text
                as="p"
                className="mt-[18px] text-[9.5px] font-medium uppercase leading-[11.5px] tracking-[1.33px] text-theme-text-muted"
              >
                {task.hint.label}
              </Text>
              <Heading
                as="h3"
                className="mt-[13px] max-w-[150px] text-[19px] font-extrabold leading-[22.8px] tracking-[-0.855px] text-theme-text-heading"
              >
                {task.hint.title}
              </Heading>
              <Text
                as="p"
                className="mt-3.5 text-[10.5px] leading-[16.8px] text-theme-text-secondary"
              >
                {task.hint.text}
              </Text>
              {task.hint.footer && (
                <Text
                  as="p"
                  className="mt-[18px] border-l-2 border-primary-purple pl-3.5 text-[10.5px] leading-[16.8px] text-theme-text-secondary"
                >
                  {task.hint.footer}
                </Text>
              )}
            </aside>
          ) : (
            // Figma 338:4199 — glyph on top, footer set off by a purple rule.
            <aside className="hidden w-[248px] flex-col self-start rounded-2xl border border-[color-mix(in_srgb,var(--color-primary-purple)_18%,transparent)] bg-[color-mix(in_srgb,var(--color-primary-purple)_8%,var(--theme-bg-surface))] px-[19px] pb-7 pt-[26px] md:flex">
              <span aria-hidden className="text-[22px] leading-[22px] text-primary-purple">
                {task.hint.glyph ?? "↗"}
              </span>
              <Text
                as="p"
                className="mt-[18px] text-[9.5px] font-medium uppercase leading-[11.5px] tracking-[1.33px] text-theme-text-muted"
              >
                {task.hint.label}
              </Text>
              <Heading
                as="h3"
                className="mt-[13px] max-w-[150px] text-[19px] font-extrabold leading-[22.8px] tracking-[-0.855px] text-theme-text-heading"
              >
                {task.hint.title}
              </Heading>
              <Text
                as="p"
                className="mt-3.5 text-[10.5px] leading-[16.8px] text-theme-text-secondary"
              >
                {task.hint.text}
              </Text>
              <Text
                as="p"
                className="mt-[18px] border-l-2 border-primary-purple pl-3.5 text-[10.5px] leading-[16.8px] text-theme-text-secondary"
              >
                {task.hint.footer}
              </Text>
            </aside>
          )}
          {asideBlocks.length > 0 && (
            <div className="hidden md:block">
              <StepBody blocks={asideBlocks} stars={stars} dense />
            </div>
          )}
          {inHint && hintAction}
          {inSession && sessionStatus}
        </div>
      </div>

      {!inColumn && !inHint && !inSession && actionRow}
    </div>
  );
};

/* ─────────────────────────────────────────────
 *  Body blocks
 * ───────────────────────────────────────────── */

const SectionTitle = ({
  children,
  spacing,
}: {
  children: string;
  /** Figma gaps above/below: default 30/8, md 28/10, sm 15/10. */
  spacing?: "md" | "sm";
}) => (
  <Heading
    as="h3"
    className={cn(
      "text-[9.5px] font-medium uppercase leading-[11.5px] tracking-[1.52px] text-theme-text-muted",
      spacing === "md" ? "mb-2 mt-5" : spacing === "sm" ? "mb-2 mt-[11px]" : "mb-1.5 mt-[20px]",
    )}
  >
    {children}
  </Heading>
);

/** Story cards as designed (Figma 338:4207). */
const StoryCards = ({
  items,
}: {
  items: Extract<GettingStartedBlock, { kind: "storyCards" }>["items"];
}) => (
  <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-3">
    {items.map((story) => (
      <div
        key={story.tag}
        className="flex min-w-0 flex-col items-start rounded-[14px] border border-theme-border bg-theme-bg-surface px-[15px] pb-[15px] pt-[15px]"
      >
        <span className="rounded-full bg-[color-mix(in_srgb,var(--color-primary-purple)_12%,var(--theme-bg-surface))] px-2.5 py-1 text-[10px] font-semibold text-primary-purple">
          {story.tag}
        </span>
        <Text
          as="p"
          className="pt-2.5 text-[11.5px] font-semibold leading-[1.35] text-theme-text-heading"
        >
          {story.title}
        </Text>
        <Text as="p" className="mt-1.5 text-[10.5px] leading-[16.27px] text-theme-text-secondary">
          <span className="font-semibold text-theme-text-heading">Result</span> · {story.result}
        </Text>
      </div>
    ))}
  </div>
);

/** "Lead: explanation" → ["Lead", "Explanation"]; null when the item has no lead. */
const splitLead = (item: string): [string, string] | null => {
  const m = item.match(/^([^:]{2,45}):\s+([\s\S]+)$/);
  if (!m) return null;
  const rest = m[2];
  return [m[1], rest.charAt(0).toUpperCase() + rest.slice(1)];
};

const GRID_EMBLEMS = ["✦", "↗", "✳", "◷", "✎"];

const ListBlock = ({
  block,
  dense = false,
}: {
  block: Extract<GettingStartedBlock, { kind: "ul" }>;
  dense?: boolean;
}) => {
  if (block.variant === "tips") {
    return (
      <div className="mb-1 flex flex-col gap-[7px]">
        {block.items.map((item) => (
          <div key={item} className="flex items-start gap-2.5">
            <span className="text-[15px] leading-[1.15] text-primary-purple" aria-hidden>
              •
            </span>
            <Text as="span" className="text-[12px] leading-[1.62] text-theme-text-secondary">
              {item}
            </Text>
          </div>
        ))}
      </div>
    );
  }

  // The guided grid needs a lead for every item — from the item itself or its
  // label. A list without them reads better as plain bullets.
  const rows = block.items.map(
    // An explicit label wins: Figma copy such as "One lane: …" is description,
    // not a lead. Without one, an item's own "Lead: text" prefix is used.
    // A label that repeats the item's prefix ("Goal: …") drops the prefix.
    (item, i): [string, string] | null => {
      const label = block.labels?.[i];
      const split = splitLead(item);
      if (!label) return split;
      return split && split[0].toLowerCase() === label.toLowerCase()
        ? [label, split[1]]
        : [label, item];
    },
  );
  if (rows.some((r) => r === null)) {
    return (
      <div className="mb-1 flex flex-col gap-2.5">
        {block.items.map((item) => (
          <div key={item} className="flex items-start gap-2.5">
            <span className="mt-[7px] h-[5px] w-[5px] flex-shrink-0 rounded-full bg-primary-purple" />
            <Text as="span" className="text-[12.5px] leading-[1.63] text-theme-text-secondary">
              {item}
            </Text>
          </div>
        ))}
      </div>
    );
  }

  const numbered = block.variant === "numbered";
  return (
    <div className={cn("grid grid-cols-1 gap-x-4", !dense && "sm:grid-cols-2 lg:grid-cols-3")}>
      {(rows as [string, string][]).map(([lead, text], i) => (
        <div
          key={lead + i}
          className="flex min-w-0 items-start gap-3.5 border-t border-theme-border-subtle pb-2.5 pt-[11px]"
        >
          <span
            className={cn(
              "flex flex-shrink-0 items-center justify-center rounded-full bg-[color-mix(in_srgb,var(--color-primary-purple)_12%,var(--theme-bg-surface))] text-primary-purple",
              numbered
                ? "h-5 w-5 text-[9.5px] font-normal"
                : "h-[18px] w-[18px] text-[10px] font-extrabold",
            )}
            aria-hidden
          >
            {numbered ? String(i + 1).padStart(2, "0") : GRID_EMBLEMS[i % GRID_EMBLEMS.length]}
          </span>
          <div className="min-w-0">
            <Text
              as="p"
              className="text-[11.5px] font-semibold leading-[1.35] text-theme-text-heading"
            >
              {lead}
            </Text>
            <Text
              as="p"
              className="mt-1.5 break-words text-[10.5px] leading-[16.27px] text-theme-text-secondary"
            >
              {text}
            </Text>
          </div>
        </div>
      ))}
    </div>
  );
};

const NoteBlock = ({
  label,
  text,
  flush = false,
}: {
  label: string;
  text: string;
  flush?: boolean;
}) => (
  <div className={flush ? undefined : "pt-4"}>
    <div className="flex flex-wrap gap-x-7 gap-y-1 border-t border-theme-border-subtle px-4 pt-[15px]">
      <Text
        as="p"
        className="text-[10.5px] font-semibold uppercase tracking-[0.21px] text-theme-text-heading"
      >
        {label}
      </Text>
      <Text
        as="p"
        className="min-w-[240px] flex-1 text-[10.5px] leading-[16.27px] text-theme-text-secondary"
      >
        {text}
      </Text>
    </div>
  </div>
);

const QuoteBlock = ({ label, text }: { label: string; text: string }) => {
  const { showToast } = useToast();
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      showToast({ type: "success", message: "Copied to clipboard" });
    } catch {
      // Clipboard is blocked in some browsers; the wording is selectable text.
    }
  };
  return (
    <div className="max-w-[784px] pt-2.5">
      <div className="flex flex-col gap-[9.5px] rounded-[4px_14px_14px_4px] border-l-[3px] border-primary-purple bg-[color-mix(in_srgb,var(--color-primary-purple)_14%,var(--theme-bg-surface))] pb-[19.5px] pl-[23px] pr-[21px] pt-[18px]">
        <Text
          as="p"
          className="text-[9.5px] font-medium uppercase leading-[11.5px] tracking-[1.33px] text-theme-text-muted"
        >
          {label}
        </Text>
        <Text as="p" className="text-[11.5px] leading-[18.98px] text-theme-text-heading">
          {text}
        </Text>
        <button
          type="button"
          onClick={copy}
          className="inline-flex h-[12.5px] w-fit items-center gap-1 text-[10.5px] font-medium leading-none text-primary-purple hover:underline"
        >
          <Copy size={10} /> Copy wording
        </button>
      </div>
    </div>
  );
};

const StepBody = ({
  blocks,
  stars,
  sessionAction,
  dense = false,
}: {
  blocks: GettingStartedBlock[];
  stars: StarPortfolioEntry[];
  sessionAction?: ReactNode;
  dense?: boolean;
}) => {
  const { showErrorToast } = useToast();

  // The picker lists rows from the `stars` table rather than the capabilities
  // mirror: generation needs a row id, and legacy free-text stories have none.
  const hasPicker = blocks.some((b) => b.kind === "starPicker");
  const { data: portfolio = [], isLoading: portfolioLoading } = useQuery({
    queryKey: ["program-coach-stars"],
    queryFn: () => api.gettingStarted.listProgramCoachStars(),
    enabled: hasPicker,
    // Always refetched on mount: the empty state sends the user to their
    // profile to add stories, and they must be here when they come back.
    staleTime: 0,
  });
  // Which S.T.A.R. feeds the `prompt` block, and the prompt generated for
  // each. Picking a card selects it straight away; generating is a separate,
  // explicit click (every generation counts against the daily AI limit).
  // Both survive leaving the page — see coachStore — so a prompt the user
  // already paid for is never lost to a reload. Local to the step because
  // only xo.launch.design has these blocks.
  const { user } = useAuth();
  const storeKey = user?.id ? `fa_gs_program_coach_v1:${user.id}` : null;
  const [{ selectedId, generated }, setCoach] = useState(() => coachStore.read(storeKey));
  useEffect(() => setCoach(coachStore.read(storeKey)), [storeKey]);
  const updateCoach = (patch: Partial<CoachState>) =>
    setCoach((prev) => {
      const next = { ...prev, ...patch };
      coachStore.write(storeKey, next);
      return next;
    });

  const selectedIdx = portfolio.findIndex((s) => s.id === selectedId);
  const selectedPrompt = selectedId !== null ? generated[selectedId] : undefined;

  const { data: aiUsage } = useQuery({
    queryKey: ["ai-usage-me"],
    queryFn: () => api.users.getAiUsage(),
    enabled: hasPicker,
    staleTime: 0,
  });
  const remaining = aiUsage?.daily.remaining;

  const queryClient = useQueryClient();
  const generate = useMutation({
    mutationFn: (starId: number) => api.gettingStarted.generateProgramCoachPrompt(starId),
    onSuccess: (out, starId) => {
      trackGettingStarted(
        starId in generated ? "program_coach_prompt_regenerated" : "program_coach_prompt_generated",
        { star_id: starId },
      );
      updateCoach({ generated: { ...generated, [out.starId]: out } });
    },
    onError: (err, starId) => {
      trackGettingStarted("program_coach_prompt_failed", {
        star_id: starId,
        regenerate: starId in generated,
      });
      const detail = axios.isAxiosError(err) ? err.response?.data?.detail : undefined;
      showErrorToast(
        typeof detail === "string" ? detail : "Could not generate your prompt. Please try again.",
      );
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: ["ai-usage-me"] }),
  });

  const selectedStar = portfolio[selectedIdx];
  const selectedTitle =
    selectedStar?.title?.trim() || selectedStar?.situation?.trim().slice(0, 80) || undefined;

  return (
    <div>
      {blocks.map((block, i) => {
        switch (block.kind) {
          case "h":
            return (
              <SectionTitle key={i} spacing={block.spacing}>
                {block.text}
              </SectionTitle>
            );

          case "storyCards":
            return <StoryCards key={i} items={block.items} />;

          case "letterCards":
            return (
              <div key={i} className="grid grid-cols-2 gap-[14px] lg:grid-cols-4">
                {block.items.map(({ word, text }) => (
                  // SUPERSEDED: bg-[#F3EEE8] -- a fixed light card, so in dark
                  // mode the light heading text vanished into it.
                  <div
                    key={word}
                    className="flex flex-col gap-1.5 self-start rounded-[14px] border border-theme-border-subtle bg-theme-bg-surface-raised px-3 pb-[13px] pt-2.5"
                  >
                    <p className="leading-[28px]">
                      <span className="text-[30px] font-bold text-primary-purple">{word[0]}</span>
                      <span className="text-[15px] font-semibold text-theme-text-heading">
                        {word.slice(1)}
                      </span>
                    </p>
                    <Text
                      as="p"
                      className="text-[9.5px] leading-[14.73px] text-theme-text-secondary"
                    >
                      {text}
                    </Text>
                  </div>
                ))}
              </div>
            );

          case "note":
            return <NoteBlock key={i} label={block.label} text={block.text} />;

          case "p":
            return (
              <Text key={i} as="p" className="mb-3 text-[12px] leading-[1.6] text-theme-text-muted">
                {block.text}
              </Text>
            );

          case "ul":
            return <ListBlock key={i} block={block} dense={dense} />;

          case "quote":
            return <QuoteBlock key={i} label={block.label} text={block.text} />;

          case "contrast":
            return (
              <div key={i} className="mt-[18px]">
                <div className="grid grid-cols-1 gap-[18px] sm:grid-cols-2">
                  {/* SUPERSEDED: bg-[#E7EEE5] / bg-[#F8EBE4] -- fixed light fills
                      that stayed light in dark mode. */}
                  <div className="rounded-xl bg-theme-bg-success px-[15px] py-3.5 text-[10.5px] leading-[16.27px] text-theme-text-secondary">
                    <span className="mb-[7px] block text-[9.5px] font-medium uppercase leading-[11.5px] tracking-[1.33px] text-theme-text-success">
                      {block.goodLabel ?? "What counts"}
                    </span>
                    {block.good.map((line) => (
                      <span key={line} className="block">
                        {line}
                      </span>
                    ))}
                  </div>
                  <div className="rounded-xl bg-theme-bg-error px-[15px] py-3.5 text-[10.5px] leading-[16.27px] text-theme-text-secondary">
                    <span className="mb-[7px] block text-[9.5px] font-medium uppercase leading-[11.5px] tracking-[1.33px] text-theme-text-error">
                      {block.badLabel ?? "Not enough"}
                    </span>
                    {block.bad.map((line) => (
                      <span key={line} className="block">
                        {line}
                      </span>
                    ))}
                  </div>
                </div>
                {block.note && (
                  <Text as="p" className="mt-2.5 text-[11.5px] leading-[1.6] text-theme-text-muted">
                    {block.note}
                  </Text>
                )}
              </div>
            );

          case "session":
            return (
              <div
                key={i}
                className="mt-[15px] flex flex-wrap items-center gap-3 rounded-[14px] border border-theme-accent-subtle bg-theme-accent-subtle px-[15px] py-[15px]"
              >
                <CalendarDays size={16} className="flex-shrink-0 text-primary-purple" />
                <div className="min-w-0">
                  <Text
                    as="p"
                    className="text-[11.5px] font-semibold leading-[1.35] text-theme-text-heading"
                  >
                    {block.title}
                  </Text>
                  <Text
                    as="p"
                    className="mt-1.5 text-[10.5px] leading-[16.27px] text-theme-text-secondary"
                  >
                    {block.meta}
                  </Text>
                </div>
                {sessionAction && <div className="ml-auto">{sessionAction}</div>}
              </div>
            );

          case "prompt":
            return (
              <PromptBlock
                key={i}
                text={block.text}
                star={portfolio[selectedIdx]}
                generated={selectedPrompt}
                onCopied={() =>
                  trackGettingStarted("program_coach_prompt_copied", {
                    star_id: selectedId,
                    ai_drafted: !!selectedPrompt,
                  })
                }
                // Only a picker step generates; elsewhere the block is static.
                generator={
                  hasPicker
                    ? {
                        starSelected: selectedIdx >= 0,
                        isGenerating: generate.isPending,
                        outOfCredits: remaining === 0,
                        starTitle: selectedTitle,
                        onGenerate: () => selectedId !== null && generate.mutate(selectedId),
                      }
                    : undefined
                }
              />
            );

          case "stars":
            return <StarList key={i} stars={stars} />;

          case "onboardingChecklist":
            return <OnboardingChecklist key={i} />;

          case "starPicker":
            // Nothing while loading, so "No S.T.A.R. stories yet" does not
            // flash before the user's stories arrive.
            return portfolioLoading ? null : (
              <StarList
                key={i}
                stars={portfolio}
                pickedIdx={selectedIdx}
                onPick={(idx) => {
                  const starId = portfolio[idx]?.id ?? null;
                  if (starId !== null && starId !== selectedId) {
                    trackGettingStarted("program_coach_star_selected", {
                      star_id: starId,
                      has_prompt: starId in generated,
                    });
                  }
                  updateCoach({ selectedId: starId });
                }}
              />
            );

          default:
            return null;
        }
      })}
    </div>
  );
};

/**
 * The picked S.T.A.R. and the prompts generated so far, per user, in
 * localStorage. Generated prompts cost AI credits, so losing them on a reload
 * would charge the user again for the same thing. Every access is guarded —
 * storage throws in private windows — and a failure just means starting fresh.
 */
interface CoachState {
  selectedId: number | null;
  generated: Record<number, ProgramCoachPromptOut>;
}
const EMPTY_COACH: CoachState = { selectedId: null, generated: {} };
const coachStore = {
  read(key: string | null): CoachState {
    if (!key) return EMPTY_COACH;
    try {
      const parsed = JSON.parse(localStorage.getItem(key) ?? "null") as Partial<CoachState> | null;
      return {
        selectedId: typeof parsed?.selectedId === "number" ? parsed.selectedId : null,
        generated:
          parsed?.generated && typeof parsed.generated === "object" ? parsed.generated : {},
      };
    } catch {
      return EMPTY_COACH;
    }
  },
  write(key: string | null, state: CoachState) {
    if (!key) return;
    try {
      localStorage.setItem(key, JSON.stringify(state));
    } catch {
      /* storage unavailable — the choice lasts until the page closes */
    }
  },
};

/** A one-line summary of a S.T.A.R., for substituting into the AI prompt. */
const starSummary = (star: StarPortfolioEntry | undefined): string => {
  if (!star) return "[describe the experience this program is built on]";
  if (!isStructuredStar(star)) return star;
  const story = star as StarPortfolioStory;
  return [story.title, story.result].filter(Boolean).join(" — ") || story.situation;
};

/** The prompt with every token filled — from the AI output when there is one. */
const resolvePrompt = (
  text: string,
  star: StarPortfolioEntry | undefined,
  generated: ProgramCoachPromptOut | undefined,
): string =>
  PROGRAM_COACH_PROMPT_TOKENS.reduce(
    (out, { token, field, fallback }) =>
      out.split(token).join(generated?.[field] || (fallback ?? starSummary(star))),
    text,
  );

interface PromptGenerator {
  starSelected: boolean;
  isGenerating: boolean;
  outOfCredits: boolean;
  /** The picked S.T.A.R.'s heading, shown under the button so a switch is visible. */
  starTitle?: string;
  onGenerate: () => void;
}

const PromptBlock = ({
  text,
  star,
  generated,
  generator,
  onCopied,
}: {
  text: string;
  star?: StarPortfolioEntry;
  generated?: ProgramCoachPromptOut;
  /** Analytics hook, after a successful copy. */
  onCopied?: () => void;
  /**
   * Set on the picker step. Until the selected S.T.A.R. has a generated
   * prompt, the box holds a centred Generate button instead of the example
   * prompt; once it has one, the prompt shows with a small Regenerate.
   */
  generator?: PromptGenerator;
}) => {
  const [copied, setCopied] = useState(false);
  const resolved = resolvePrompt(text, star, generated);

  if (generator && !generated) {
    return (
      <div className="my-2 flex min-h-[180px] flex-col items-center justify-center gap-3 rounded-xl border border-dashed border-theme-border-strong bg-theme-accent-subtle px-5 py-8 text-center">
        <button
          type="button"
          onClick={generator.onGenerate}
          disabled={!generator.starSelected || generator.isGenerating || generator.outOfCredits}
          className={STEP_BUTTON}
        >
          {generator.isGenerating ? (
            <Loader2 size={14} className="animate-spin" />
          ) : (
            <Sparkles size={14} />
          )}
          {generator.isGenerating ? "Generating…" : "Generate prompt"}
        </button>
        {generator.starSelected ? (
          <div className="flex max-w-[52ch] flex-col items-center gap-0.5">
            <Text
              as="p"
              className="text-[10.5px] font-bold uppercase tracking-[0.1em] text-theme-text-muted"
            >
              Based on
            </Text>
            <Text
              as="p"
              className="text-[13px] font-semibold leading-[1.45] text-theme-text-heading"
            >
              {generator.starTitle ?? "Your selected S.T.A.R."}
            </Text>
          </div>
        ) : (
          <Text as="p" className="max-w-[46ch] text-[12px] leading-[1.6] text-theme-text-muted">
            Select a S.T.A.R. above, then generate a prompt drafted from it.
          </Text>
        )}
        {/* The button is disabled with no credits left — say why. */}
        {generator.outOfCredits && (
          <Text as="p" className="text-[12px] text-theme-text-muted">
            You&apos;ve used all of today&apos;s AI generations. Try again tomorrow.
          </Text>
        )}
      </div>
    );
  }

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(resolved);
      setCopied(true);
      onCopied?.();
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard is blocked in some browsers and over plain http. The prompt
      // is selectable text either way, so failing quietly beats an error toast
      // for something the user can still do by hand.
    }
  };

  return (
    <div className="my-2 rounded-xl border border-theme-accent-subtle bg-theme-accent-subtle p-3.5">
      {generated && (
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-theme-bg-surface px-2.5 py-1 text-[10.5px] font-bold text-theme-badge-accent-text">
            <Sparkles size={10} /> AI-drafted from your S.T.A.R.
          </span>
          {generator && (
            <button
              type="button"
              onClick={generator.onGenerate}
              disabled={generator.isGenerating || generator.outOfCredits}
              className="inline-flex items-center gap-1.5 rounded-lg border border-theme-border bg-theme-bg-surface px-2.5 py-1 text-[11px] font-semibold text-theme-text-secondary transition-colors hover:bg-theme-bg-hover disabled:cursor-not-allowed disabled:opacity-50"
            >
              {generator.isGenerating ? (
                <Loader2 size={11} className="animate-spin" />
              ) : (
                <RefreshCw size={11} />
              )}
              {generator.isGenerating ? "Regenerating…" : "Regenerate"}
            </button>
          )}
        </div>
      )}
      <pre className="whitespace-pre-wrap font-mono text-[12px] leading-[1.55] text-theme-text-secondary">
        {resolved}
      </pre>
      <div className="flex justify-end pt-2.5">
        <button
          type="button"
          onClick={copy}
          className="inline-flex items-center gap-1.5 rounded-[9px] border border-theme-border bg-theme-bg-surface px-[11px] py-2 text-[11px] font-semibold text-primary-purple transition-colors hover:bg-theme-bg-hover"
        >
          {copied ? <Check size={12} /> : <Copy size={12} />}
          {copied ? "Copied" : "Copy prompt"}
        </button>
      </div>
    </div>
  );
};

/** Profile → Expert Contribution, scrolled to the S.T.A.R. Portfolio card. */
const STAR_PORTFOLIO_PATH = "/profile?tab=star-portfolio#star-portfolio";

const StarList = ({
  stars,
  pickedIdx,
  onPick,
}: {
  stars: StarPortfolioEntry[];
  pickedIdx?: number;
  onPick?: (idx: number) => void;
}) => {
  const navigate = useNavigate();

  if (stars.length === 0) {
    return (
      <div className="my-1.5 flex flex-col items-start gap-3 rounded-xl border border-dashed border-theme-border-strong bg-theme-bg-surface px-4 py-3.5">
        <Text as="p" className="text-[12.5px] leading-[1.65] text-theme-text-muted">
          No S.T.A.R. stories yet. Add one to your Expert Contribution and it will appear here.
        </Text>
        <button
          type="button"
          onClick={() => {
            trackGettingStarted("add_star_clicked", { source: "empty_state" });
            navigate(STAR_PORTFOLIO_PATH);
          }}
          className="inline-flex items-center gap-1.5 rounded-[10px] border border-theme-accent-subtle bg-theme-accent-subtle px-[13px] py-2 text-[12px] font-semibold text-theme-badge-accent-text transition-opacity hover:opacity-90"
        >
          <Plus size={13} /> Add S.T.A.R.s
        </button>
      </div>
    );
  }

  return (
    // auto-fill keeps empty tracks, so one or two stories do not stretch
    // across the whole step; the max() caps it at three up.
    <div className="my-1.5 grid grid-cols-[repeat(auto-fill,minmax(max(200px,calc((100%_-_20px)/3)),1fr))] gap-2.5">
      {stars.map((star, i) => {
        const structured = isStructuredStar(star) ? (star as StarPortfolioStory) : null;
        const title = structured?.title || (structured ? structured.situation : String(star));
        const result = structured?.result;
        const selected = onPick !== undefined && i === pickedIdx;

        const inner = (
          <>
            <Text
              as="p"
              className="line-clamp-2 text-[12.5px] font-bold leading-[1.4] text-theme-text-heading"
            >
              {title}
            </Text>
            {result && (
              <Text
                as="p"
                className="line-clamp-3 text-[11.5px] leading-[1.55] text-theme-text-muted"
              >
                <span className="font-bold text-theme-text-secondary">Result · </span>
                {result}
              </Text>
            )}
            {selected && (
              <span className="mt-auto inline-flex items-center gap-1 text-[11px] font-bold text-primary-purple">
                <Check size={11} strokeWidth={3} /> In prompt
              </span>
            )}
          </>
        );

        const shared = cn(
          "flex w-full min-w-0 flex-col items-start gap-[9px] rounded-xl bg-theme-bg-surface p-3.5 text-left",
          selected
            ? "border-2 border-primary-purple bg-theme-accent-subtle p-[13px]"
            : "border border-theme-border",
        );

        return onPick ? (
          <button
            key={i}
            type="button"
            onClick={() => onPick(i)}
            className={cn(shared, !selected && "transition-colors hover:bg-theme-bg-hover")}
          >
            {inner}
          </button>
        ) : (
          <div key={i} className={shared}>
            {inner}
          </div>
        );
      })}
      {/* Picker only: add a story without leaving the reader guessing where.
          Opens Profile → Expert Contribution scrolled to the S.T.A.R. card. */}
      {onPick && (
        <button
          type="button"
          onClick={() => {
            trackGettingStarted("add_star_clicked", { source: "program_coach_picker" });
            navigate(STAR_PORTFOLIO_PATH);
          }}
          className="flex w-full flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-theme-border-strong px-3.5 py-3.5 text-center text-theme-text-secondary transition-colors hover:border-primary-purple hover:text-primary-purple"
        >
          <span className="flex h-8 w-8 items-center justify-center rounded-full bg-theme-accent-subtle text-primary-purple">
            <Plus size={15} />
          </span>
          <Text as="span" className="text-[12.5px] font-semibold">
            Add another S.T.A.R.
          </Text>
        </button>
      )}
    </div>
  );
};

/* ─────────────────────────────────────────────
 *  Inline referral progress (Phase 3)
 * ───────────────────────────────────────────── */

/**
 * The referrals so far for one kind. Reads the same query the modal writes, so
 * adding someone updates the box and the CTA label without a refetch.
 */
const useReferralList = (kind: "achiever" | "xo" | undefined) => {
  const { data } = useQuery({
    queryKey: ["getting-started-referrals"],
    queryFn: () => api.gettingStarted.getReferrals(),
    staleTime: 60 * 1000,
    enabled: !!kind,
  });
  if (!kind) return [];
  return (kind === "xo" ? data?.xos : data?.achievers) ?? [];
};

/** The step's own view of its referrals: how many of three, and who they are. */
const ReferralProgress = ({ kind }: { kind: "achiever" | "xo" }) => {
  const list = useReferralList(kind);
  const label = kind === "xo" ? "Expert Operators" : "Achievers";

  return (
    <div className="mt-[15px] flex flex-wrap items-center gap-[15px] rounded-xl border border-theme-border bg-theme-bg-surface p-3.5">
      <div className="flex min-w-[200px] flex-1 flex-col gap-1.5">
        <Text as="span" className="text-[12.5px] font-bold text-theme-text-heading">
          {label} · {Math.min(list.length, REFERRALS_TARGET)} of {REFERRALS_TARGET} referred
        </Text>
        {/* Three is the ask; one already ticks the step. Saying so stops the
            two remaining segments reading as work still owed. */}
        {list.length >= REFERRALS_TO_COMPLETE && list.length < REFERRALS_TARGET && (
          <Text as="span" className="text-[11px] text-theme-text-success">
            Step complete — add more if you can
          </Text>
        )}
        {list.length > 0 && (
          <div className="mt-1 flex flex-wrap gap-1.5">
            {list.map((r) => (
              <span
                key={r.id}
                className="rounded-full bg-theme-accent-subtle px-2.5 py-0.5 text-[11px] text-theme-text-secondary"
              >
                {r.name}
              </span>
            ))}
          </div>
        )}
      </div>
      <div className="ml-auto flex w-[110px] gap-1" aria-hidden>
        {Array.from({ length: REFERRALS_TARGET }).map((_, i) => (
          <span
            key={i}
            className={cn(
              "h-[5px] flex-1 rounded-full",
              i < list.length ? "bg-primary-purple" : "bg-theme-border-subtle",
            )}
          />
        ))}
      </div>
    </div>
  );
};
